import { Alert, Button, List, Loader, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlayerPlayFilled } from "@tabler/icons-react";
import { processEOM } from "api/statement.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import React, { useState } from "react";
import AdminDatePeriodPicker from "./components/AdminDatePeriodPicker";

const AdminEOMPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleProcess = async () => {
    if (!selectedDate) {
      notifications.show({
        title: "No Month Selected",
        message: "Please select a month before running Month End.",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      return;
    }

    setProcessing(true);
    try {
      await processEOM(selectedDate);

      notifications.show({
        title: "Month End Complete",
        message: `Month End for ${selectedDate} has been successfully processed.`,
        color: MantineNotificationColor.Success,
        position: MantineNotificationPosition.TopCenter,
      });
    } catch (error: any) {
      notifications.show({
        title: "Month End Failed",
        message:
          error?.response?.data?.error ||
          error?.message ||
          "An unexpected error occurred.",
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Alert
        color="red"
        title="⚠️ Month End Processing Warning"
        variant="filled"
        radius="md"
        mb="lg"
      >
        <Text fw={500} mb="sm">
          Before running Month End, please ensure you have run the{" "}
          <a href="statement-invoices">Email Statement/Invoices</a> first. Once
          this process begins, it <strong>cannot be reversed</strong>.
        </Text>

        <Text fw={700} mt="md">
          Important Notes
        </Text>

        <List spacing="xs" mt="xs">
          <List.Item>
            <strong>Date selection:</strong> The specific day you choose does
            not matter. Only the <strong>month and year</strong> are used. For
            example, selecting <em>01/01/2026</em> or <em>10/01/2026</em> will
            both process January 2026, using the last day of that month (
            <em>31/01/2013</em>).
          </List.Item>

          <List.Item>
            <strong>Statements and Invoices:</strong> Please confirm you have
            printed all Statements and Invoices before running Month End. These
            documents cannot be regenerated once Month End is complete.
          </List.Item>
        </List>
      </Alert>
      <Stack mt="lg" gap="md">
        <AdminDatePeriodPicker
          disabled={processing}
          value={selectedDate}
          onChange={setSelectedDate}
        />
        <Button
          w={300}
          disabled={processing}
          size="lg"
          color="red"
          leftSection={
            processing ? (
              <Loader size={18} type="dots" />
            ) : (
              <IconPlayerPlayFilled size={18} />
            )
          }
          loaderProps={{ size: 20 }} // <— fixes clipping
          onClick={handleProcess}
        >
          Process Month End
        </Button>
      </Stack>
    </>
  );
};

export default AdminEOMPage;
